import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './CardNav.css';
import loopinoLogo from '../../../assets/images/LOOPINO-logo-new.svg';
import iconLogout from '../../../assets/images/icons/icon-logout.svg';
import desktopNavSections from '../navItems';
import { useCampaign } from '../../CampaignProvider/CampaignProvider';
import useCompany from '../../../hooks/useCompany';
import DesktopAIBattery from './DesktopAIBattery';
import { useAuthContext } from '../../../context/AuthContext';
import { useCapabilities } from '../../../capabilities/CapabilityContext';
import useLanguage from '../../../hooks/useLanguage';
import { API_CONFIG } from '../../../config/api';
import { migrateLegacyMediaUrlsInLocalStorage, normalizeMediaUrl } from '../../../utils/normalizeMediaUrl';

const DEFAULT_ITEMS = desktopNavSections;
const HOME_PATH = '/desktop/posts';
const PROFILE_PATH = '/desktop/profile';
const isBrowser = typeof window !== 'undefined';
const hasDocument = typeof document !== 'undefined';
const LOGO_STORAGE_KEYS = {
  preview: 'companyLogoPreview',
  name: 'companyLogoName',
  objectFit: 'companyLogoObjectFit',
  companyId: 'companyLogoCompanyId',
  remoteUrl: 'companyLogoRemoteUrl',
  updatedAt: 'companyLogoPreviewUpdatedAt'
};

const buildStrategizerDnaPath = (companyId) => {
  const params = new URLSearchParams();
  params.set('view', 'dna');
  if (companyId) {
    params.set('companyId', companyId);
  }
  return `/strategizer?${params.toString()}`;
};

const ArrowUpRightIcon = ({ 'aria-hidden': ariaHidden = true }) => (
  <svg
    className="nav-card-link-icon"
    aria-hidden={ariaHidden}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 17L17 7M17 7H8M17 7V16"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UserPlaceholderIcon = ({ 'aria-hidden': ariaHidden = true, className = '' }) => (
  <svg
    className={className || undefined}
    aria-hidden={ariaHidden}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="12"
      cy="8"
      r="4"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M20 21a8 8 0 0 0-16 0"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const PROFILE_AVATAR = {
  src: 'https://coss.com/origin/avatar-80-07.jpg',
  fallback: 'KK'
};

const ProfileAvatar = ({
  fallback = PROFILE_AVATAR.fallback,
  showUserPlaceholder = false,
  isAuthenticated = false,
  userId = null,
  onLogoDataChange,
  onCompanyDataChange
}) => {
  const { t } = useLanguage();
  let campaignContext = null;
  try {
    campaignContext = useCampaign();
  } catch (error) {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[CardNav] CardNav rendered outside CampaignProvider. Active campaign data unavailable.');
    }
  }

  const activeCampaign = campaignContext?.activeCampaign;
  const { getCompanyDetails, currentCompany, loadCompanies } = useCompany();
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [uploadedLogo, setUploadedLogo] = useState(() => {
    if (!isBrowser) return null;
    const logoPreview = normalizeMediaUrl(window.localStorage.getItem(LOGO_STORAGE_KEYS.preview), API_CONFIG.BASE_URL);
    const logoName = window.localStorage.getItem(LOGO_STORAGE_KEYS.name);
    const storedCompanyId = window.localStorage.getItem(LOGO_STORAGE_KEYS.companyId);
    const storedRemoteUrl = normalizeMediaUrl(window.localStorage.getItem(LOGO_STORAGE_KEYS.remoteUrl), API_CONFIG.BASE_URL);
    const storedUpdatedAtRaw = window.localStorage.getItem(LOGO_STORAGE_KEYS.updatedAt);
    const storedUpdatedAt = storedUpdatedAtRaw ? Number(storedUpdatedAtRaw) : null;

    return logoPreview && logoName
      ? {
          preview: logoPreview,
          name: logoName,
          companyId: storedCompanyId || null,
          remoteUrl: storedRemoteUrl || null,
          updatedAt: Number.isFinite(storedUpdatedAt) ? storedUpdatedAt : null
        }
      : null;
  });
  const [logoObjectFit, setLogoObjectFit] = useState(() => {
    if (!isBrowser) return 'cover';
    return window.localStorage.getItem(LOGO_STORAGE_KEYS.objectFit) || 'cover';
  });
  const [loadedCompanyId, setLoadedCompanyId] = useState(null);
  const [imageError, setImageError] = useState(false);
  const prevUserIdRef = useRef(userId);
  const lastCompaniesLoadRef = useRef(null);

  const isPlaceholderUrl = useCallback((value) => {
    if (typeof value !== 'string') return false;
    return value.toLowerCase().includes('placeholder');
  }, []);

  const isDataUrl = useCallback((value) => {
    return typeof value === 'string' && value.startsWith('data:');
  }, []);

  const isBlobUrl = useCallback((value) => {
    return typeof value === 'string' && value.startsWith('blob:');
  }, []);

  const isValidLogoPreviewUrl = useCallback(
    (value) => {
      if (typeof value !== 'string') return false;
      if (!value.trim()) return false;
      if (isPlaceholderUrl(value)) return false;
      return true;
    },
    [isPlaceholderUrl]
  );

  const isValidBackendLogoUrl = useCallback(
    (value) => {
      if (!isValidLogoPreviewUrl(value)) return false;
      if (isDataUrl(value)) return false;
      if (isBlobUrl(value)) return false;
      return true;
    },
    [isBlobUrl, isDataUrl, isValidLogoPreviewUrl]
  );

  const resolveLogoUrl = useCallback((data) => {
    if (!data) return null;

    const companyInfo = data.company || data;
    const logo = companyInfo.logo;

    if (Array.isArray(logo) && logo.length > 0) {
      const latest = logo[logo.length - 1];
      if (latest && typeof latest === 'object') {
        if (latest.url) return latest.url;
        if (latest.preview) return latest.preview;
      }
    }

    if (logo && typeof logo === 'object' && !Array.isArray(logo)) {
      if (logo.url) return logo.url;
      if (logo.preview) return logo.preview;
    }

    if (typeof logo === 'string') {
      return logo;
    }

    if (Array.isArray(companyInfo.brand_images) && companyInfo.brand_images.length > 0) {
      const firstImage = companyInfo.brand_images[0];
      if (firstImage && typeof firstImage === 'object') {
        if (firstImage.url) return firstImage.url;
        if (firstImage.preview) return firstImage.preview;
      }
      if (typeof firstImage === 'string') {
        return firstImage;
      }
    }

    return null;
  }, []);

  const setStoredLogo = useCallback((nextLogo) => {
    if (!isBrowser || !nextLogo?.preview) return;
    const normalizedPreview = normalizeMediaUrl(nextLogo.preview, API_CONFIG.BASE_URL);
    const normalizedRemoteUrl = normalizeMediaUrl(nextLogo.remoteUrl, API_CONFIG.BASE_URL);
    window.localStorage.setItem(LOGO_STORAGE_KEYS.preview, normalizedPreview);
    window.localStorage.setItem(LOGO_STORAGE_KEYS.name, nextLogo.name || 'logo');
    if (nextLogo.companyId) {
      window.localStorage.setItem(LOGO_STORAGE_KEYS.companyId, nextLogo.companyId);
    } else {
      window.localStorage.removeItem(LOGO_STORAGE_KEYS.companyId);
    }
    if (normalizedRemoteUrl) {
      window.localStorage.setItem(LOGO_STORAGE_KEYS.remoteUrl, normalizedRemoteUrl);
    } else {
      window.localStorage.removeItem(LOGO_STORAGE_KEYS.remoteUrl);
    }
    if (typeof nextLogo.updatedAt === 'number' && Number.isFinite(nextLogo.updatedAt)) {
      window.localStorage.setItem(LOGO_STORAGE_KEYS.updatedAt, String(nextLogo.updatedAt));
    } else {
      window.localStorage.removeItem(LOGO_STORAGE_KEYS.updatedAt);
    }
  }, []);

  const clearStoredLogo = useCallback(() => {
    if (!isBrowser) return;
    window.localStorage.removeItem(LOGO_STORAGE_KEYS.preview);
    window.localStorage.removeItem(LOGO_STORAGE_KEYS.name);
    window.localStorage.removeItem(LOGO_STORAGE_KEYS.companyId);
    window.localStorage.removeItem(LOGO_STORAGE_KEYS.remoteUrl);
    window.localStorage.removeItem(LOGO_STORAGE_KEYS.updatedAt);
  }, []);

  const syncLogoFromBackend = useCallback(
    (backendLogoUrl, companyName, companyId) => {
      const normalizedBackendLogoUrl = normalizeMediaUrl(backendLogoUrl, API_CONFIG.BASE_URL);
      if (!isValidBackendLogoUrl(normalizedBackendLogoUrl)) return;

      setUploadedLogo((prev) => {
        const prevPreview = prev?.preview;
        const prevRemoteUrl = prev?.remoteUrl || (isBrowser ? window.localStorage.getItem(LOGO_STORAGE_KEYS.remoteUrl) : null);
        const hasPendingLocalPreview = isDataUrl(prevPreview);

        if (hasPendingLocalPreview && !prevRemoteUrl) {
          setStoredLogo({
            preview: prevPreview,
            name: prev?.name || companyName || 'logo',
            companyId: companyId || prev?.companyId || null,
            remoteUrl: normalizedBackendLogoUrl,
            updatedAt: prev?.updatedAt || Date.now()
          });
          return prev;
        }

        if (hasPendingLocalPreview && prevRemoteUrl && normalizedBackendLogoUrl === prevRemoteUrl) {
          setStoredLogo({
            preview: prevPreview,
            name: prev?.name || companyName || 'logo',
            companyId: companyId || prev?.companyId || null,
            remoteUrl: prevRemoteUrl,
            updatedAt: prev?.updatedAt || Date.now()
          });
          return prev;
        }

        if (
          prevPreview === normalizedBackendLogoUrl &&
          (companyId ? prev?.companyId === companyId : true) &&
          (prev?.remoteUrl ? prev.remoteUrl === normalizedBackendLogoUrl : true)
        ) {
          return prev;
        }

        const nextLogo = {
          preview: normalizedBackendLogoUrl,
          name: companyName || prev?.name || 'logo',
          companyId: companyId || prev?.companyId || null,
          remoteUrl: normalizedBackendLogoUrl,
          updatedAt: null
        };
        setStoredLogo(nextLogo);
        return nextLogo;
      });
    },
    [isDataUrl, isValidBackendLogoUrl, setStoredLogo]
  );

  useEffect(() => {
    if (!isBrowser) return;

    const loadUploadedLogo = () => {
      if (!isAuthenticated) {
        setUploadedLogo(null);
        return;
      }
      migrateLegacyMediaUrlsInLocalStorage(
        [LOGO_STORAGE_KEYS.preview, LOGO_STORAGE_KEYS.remoteUrl],
        API_CONFIG.BASE_URL
      );

      const logoPreview = normalizeMediaUrl(window.localStorage.getItem(LOGO_STORAGE_KEYS.preview), API_CONFIG.BASE_URL);
      const logoName = window.localStorage.getItem(LOGO_STORAGE_KEYS.name);
      const storedCompanyId = window.localStorage.getItem(LOGO_STORAGE_KEYS.companyId);
      const storedRemoteUrl = normalizeMediaUrl(window.localStorage.getItem(LOGO_STORAGE_KEYS.remoteUrl), API_CONFIG.BASE_URL);
      const storedUpdatedAtRaw = window.localStorage.getItem(LOGO_STORAGE_KEYS.updatedAt);
      const storedUpdatedAt = storedUpdatedAtRaw ? Number(storedUpdatedAtRaw) : null;
      if (logoPreview && logoName) {
        setUploadedLogo({
          preview: logoPreview,
          name: logoName,
          companyId: storedCompanyId || null,
          remoteUrl: storedRemoteUrl || null,
          updatedAt: Number.isFinite(storedUpdatedAt) ? storedUpdatedAt : null
        });
      } else {
        setUploadedLogo(null);
      }
    };

    loadUploadedLogo();

    const handleLogoChange = (event) => {
      if (!isAuthenticated) {
        setUploadedLogo(null);
        return;
      }
      const { preview, name, companyId: eventCompanyId, remoteUrl, updatedAt } = event.detail || {};
      const normalizedPreview = normalizeMediaUrl(preview, API_CONFIG.BASE_URL);
      const normalizedRemoteUrl = normalizeMediaUrl(remoteUrl, API_CONFIG.BASE_URL);
      if (normalizedPreview && name && isValidLogoPreviewUrl(normalizedPreview)) {
        const resolvedCompanyId = eventCompanyId || activeCompanyId || uploadedLogo?.companyId || null;
        const nextUpdatedAt = typeof updatedAt === 'number' && Number.isFinite(updatedAt)
          ? updatedAt
          : (isDataUrl(normalizedPreview) ? Date.now() : null);
        const nextRemoteUrl = normalizedRemoteUrl || (isValidBackendLogoUrl(normalizedPreview) ? normalizedPreview : uploadedLogo?.remoteUrl || null);

        const nextLogo = {
          preview: normalizedPreview,
          name,
          companyId: resolvedCompanyId,
          remoteUrl: nextRemoteUrl,
          updatedAt: nextUpdatedAt
        };
        setUploadedLogo(nextLogo);
        setStoredLogo(nextLogo);
      } else {
        setUploadedLogo(null);
        clearStoredLogo();
      }
    };

    const handleLogoFitChange = (event) => {
      const { objectFit } = event.detail || {};
      if (!objectFit) return;
      setLogoObjectFit(objectFit);
      if (isBrowser) {
        window.localStorage.setItem(LOGO_STORAGE_KEYS.objectFit, objectFit);
      }
    };

    window.addEventListener('companyLogoChanged', handleLogoChange);
    window.addEventListener('companyLogoFitChanged', handleLogoFitChange);

    return () => {
      window.removeEventListener('companyLogoChanged', handleLogoChange);
      window.removeEventListener('companyLogoFitChanged', handleLogoFitChange);
    };
  }, [
    activeCompanyId,
    clearStoredLogo,
    isDataUrl,
    isValidBackendLogoUrl,
    isValidLogoPreviewUrl,
    isAuthenticated,
    setStoredLogo,
    uploadedLogo?.companyId,
    uploadedLogo?.remoteUrl
  ]);

  useEffect(() => {
    if (!activeCompanyId) return;
    if (!uploadedLogo?.companyId) return;
    if (uploadedLogo.companyId === activeCompanyId) return;
    clearStoredLogo();
    setUploadedLogo(null);
  }, [activeCompanyId, clearStoredLogo, uploadedLogo?.companyId]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearStoredLogo();
      setUploadedLogo(null);
      setCompanyData(null);
      setLoadedCompanyId(null);
      setActiveCompanyId(null);
      setImageError(false);
      prevUserIdRef.current = userId;
      lastCompaniesLoadRef.current = null;
      return;
    }

    if (userId && lastCompaniesLoadRef.current !== userId) {
      lastCompaniesLoadRef.current = userId;
      loadCompanies().catch((error) => {
        console.warn('[CardNav] Failed to load companies after auth:', error);
      });
    }

    if (prevUserIdRef.current && userId && prevUserIdRef.current !== userId) {
      clearStoredLogo();
      setUploadedLogo(null);
      setCompanyData(null);
      setLoadedCompanyId(null);
      setActiveCompanyId(null);
      setImageError(false);
    }

    prevUserIdRef.current = userId;
  }, [clearStoredLogo, isAuthenticated, loadCompanies, userId]);

  useEffect(() => {
    if (!currentCompany) {
      return;
    }

    if (activeCampaign) return;

    const backendLogoUrl = resolveLogoUrl(currentCompany);
    syncLogoFromBackend(backendLogoUrl, currentCompany?.name || 'logo', currentCompany?.id || null);
  }, [activeCampaign, currentCompany, resolveLogoUrl, syncLogoFromBackend]);

  useEffect(() => {
    const loadCompanyData = async () => {
      if (!activeCampaign) {
        setCompanyData(null);
        setLoadedCompanyId(null);
        setActiveCompanyId(null);
        return;
      }

      let companyId = null;
      if (typeof activeCampaign.company_id === 'string') {
        companyId = activeCampaign.company_id;
      } else if (
        typeof activeCampaign.company_id === 'object' &&
        activeCampaign.company_id?.id
      ) {
        companyId = activeCampaign.company_id.id;
      } else if (activeCampaign.company?.id) {
        companyId = activeCampaign.company.id;
      } else if (typeof activeCampaign.company === 'string') {
        companyId = activeCampaign.company;
      }

      setActiveCompanyId(companyId || null);

      if (!companyId || loadedCompanyId === companyId) {
        return;
      }

      setIsLoading(true);
      try {
        const company = await getCompanyDetails(companyId);
        setCompanyData(company);
        setLoadedCompanyId(companyId);

        const backendLogoUrl = resolveLogoUrl(company);
        syncLogoFromBackend(backendLogoUrl, company?.name || 'logo', companyId);
      } catch (error) {
        console.error('[CardNav] Error loading company data:', error);
        setCompanyData(null);
        setLoadedCompanyId(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadCompanyData();
  }, [
    activeCampaign?.company_id,
    activeCampaign?.company,
    clearStoredLogo,
    getCompanyDetails,
    loadedCompanyId,
    resolveLogoUrl,
    syncLogoFromBackend
  ]);

  const logoData = useMemo(() => {
    const companyFallbackName = t('desktop.nav.companyFallback', { defaultValue: 'Azienda' });
    if (!isAuthenticated) {
      return {
        src: null,
        name: companyFallbackName,
        alt: t('desktop.nav.companyLogoAltFallback', { defaultValue: 'Logo aziendale' }),
        isEmpty: true,
        companyId: null
      };
    }
    const companyName = companyData?.name || currentCompany?.name || uploadedLogo?.name || companyFallbackName;
    const backendLogoUrl = normalizeMediaUrl(resolveLogoUrl(companyData), API_CONFIG.BASE_URL);
    const effectiveCompanyId = activeCompanyId || currentCompany?.id || null;
    const storedLogoMatchesCompany = uploadedLogo?.companyId
      ? uploadedLogo.companyId === effectiveCompanyId
      : !effectiveCompanyId;
    const preferredPreview = storedLogoMatchesCompany && isValidLogoPreviewUrl(uploadedLogo?.preview)
      ? uploadedLogo.preview
      : null;

    const resolvedBackendLogo = isValidBackendLogoUrl(backendLogoUrl) ? backendLogoUrl : null;

    if (isLoading) {
      return {
        src: preferredPreview || resolvedBackendLogo,
        name: companyName || t('common.loading', { defaultValue: 'Caricamento...' }),
        alt: t('desktop.nav.companyLogoLoadingAlt', { defaultValue: 'Loading company logo' }),
        isLoading: true,
        companyId: effectiveCompanyId
      };
    }

    if (preferredPreview || resolvedBackendLogo) {
      return {
        src: preferredPreview || resolvedBackendLogo,
        name: companyName,
        alt: t('desktop.nav.companyLogoAlt', { name: companyName, defaultValue: '{{name}} Logo' }),
        companyId: effectiveCompanyId
      };
    }

    return {
      src: null,
      name: companyName || companyFallbackName,
      alt: companyData
        ? t('desktop.nav.companyLogoUnavailableAlt', {
            name: companyName,
            defaultValue: '{{name}} (Logo non disponibile)'
          })
        : t('desktop.nav.companyLogoAltFallback', { defaultValue: 'Logo aziendale' }),
      isEmpty: true,
      companyId: effectiveCompanyId
    };
  }, [
    activeCompanyId,
    companyData,
    currentCompany?.id,
    currentCompany?.name,
    isLoading,
    isAuthenticated,
    isValidBackendLogoUrl,
    isValidLogoPreviewUrl,
    resolveLogoUrl,
    t,
    uploadedLogo?.companyId,
    uploadedLogo?.name,
    uploadedLogo?.preview
  ]);

  useEffect(() => {
    setImageError(false);
  }, [logoData.src]);

  const logoSnapshot = useMemo(() => ({
    ...logoData,
    imageError,
    objectFit: logoObjectFit
  }), [logoData, imageError, logoObjectFit]);

  useEffect(() => {
    if (typeof onLogoDataChange === 'function') {
      onLogoDataChange(logoSnapshot);
    }
  }, [logoSnapshot, onLogoDataChange]);

  useEffect(() => {
    if (typeof onCompanyDataChange === 'function') {
      onCompanyDataChange(companyData || null);
    }
  }, [companyData, onCompanyDataChange]);

  return (
    <>
      {!imageError && logoData.src ? (
        <img
          src={logoData.src}
          alt={logoData.alt}
          className="card-nav-avatar-image"
          style={{ objectFit: logoObjectFit }}
          onError={() => setImageError(true)}
        />
      ) : (
        <span className="card-nav-avatar-fallback" aria-hidden="true">
          {showUserPlaceholder || logoData.isEmpty || imageError ? (
            <UserPlaceholderIcon />
          ) : (
            logoData.name?.[0]?.toUpperCase() || fallback
          )}
        </span>
      )}
    </>
  );
};

const CardNav = ({
  logo = loopinoLogo,
  logoAlt,
  items = DEFAULT_ITEMS,
  className = '',
  ctaLabel,
  onCtaClick,
  onNavigate,
  onBatteryClick,
  isAIBatteryRegenerating = false,
  showAIBatteryStatus = true
}) => {
  const { logout: authLogout, isAuthenticated, user } = useAuthContext();
  const { getNavigationStateForHref, isAccessible } = useCapabilities();
  const { t } = useLanguage();
  const [openPanel, setOpenPanel] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [profileLogoSnapshot, setProfileLogoSnapshot] = useState(null);
  const [profileCompanyData, setProfileCompanyData] = useState(null);
  const [selectedDnaId, setSelectedDnaId] = useState(null);
  const navRef = useRef(null);
  const visibleItems = useMemo(() => (
    (items || [])
      .slice(0, 3)
      .map((section) => {
        const links = (section?.links || []).filter((link) => {
          if (!link?.href || link.action === 'logout') {
            return true;
          }
          const capability = getNavigationStateForHref(link.href);
          return !capability.managed || isAccessible(capability);
        });

        if (links.length === 0) {
          return null;
        }

        return {
          ...section,
          links,
        };
      })
      .filter(Boolean)
  ), [getNavigationStateForHref, isAccessible, items]);
  const containerClasses = ['card-nav-container', className].filter(Boolean).join(' ');
  const resolvedLogoAlt = logoAlt || t('desktop.brand.logoAlt', { defaultValue: 'Loopino' });
  const resolvedCtaLabel = ctaLabel || t('desktop.nav.profileMenuAria', { defaultValue: 'Apri il menu profilo' });
  const currentDnaId = profileLogoSnapshot?.companyId || profileCompanyData?.id || 'current';
  const currentDnaName = profileLogoSnapshot?.name || profileCompanyData?.name || t('desktop.nav.companyFallback', { defaultValue: 'Azienda' });
  const profileLogoFit = profileLogoSnapshot?.objectFit || 'cover';
  const dnaCompanyId = profileCompanyData?.id ||
    profileCompanyData?._id ||
    profileCompanyData?.company_id ||
    profileLogoSnapshot?.companyId ||
    null;
  const isMenuOpen = openPanel === 'menu';
  const isProfileOpen = openPanel === 'profile';
  const isExpanded = openPanel !== null;

  const dnaOptions = useMemo(() => ([
    {
      id: currentDnaId,
      label: currentDnaName,
      src: profileLogoSnapshot?.src || null,
      isAdd: false,
      isEmpty: profileLogoSnapshot?.isEmpty || profileLogoSnapshot?.imageError
    },
    {
      id: 'add-dna',
      label: t('desktop.nav.profileMenu.addDna', { defaultValue: 'Nuovo DNA' }),
      isAdd: true
    }
  ]), [currentDnaId, currentDnaName, profileLogoSnapshot?.imageError, profileLogoSnapshot?.isEmpty, profileLogoSnapshot?.src, t]);

  const handleLogoClick = useCallback((event) => {
    setOpenPanel(null);
    if (typeof onNavigate === 'function') {
      event.preventDefault();
      onNavigate(HOME_PATH);
    } else if (isBrowser) {
      window.location.href = HOME_PATH;
    }
  }, [onNavigate]);

  const toggleMenu = () => setOpenPanel((prev) => (prev === 'menu' ? null : 'menu'));

  const toggleProfilePanel = () => setOpenPanel((prev) => (prev === 'profile' ? null : 'profile'));

  const handleCreateDna = useCallback(() => {
    setOpenPanel(null);
    const targetPath = buildStrategizerDnaPath(dnaCompanyId);
    if (typeof onNavigate === 'function') {
      onNavigate(targetPath);
    } else if (isBrowser) {
      window.location.href = targetPath;
    }
  }, [dnaCompanyId, onNavigate]);

  const handleSelectDna = useCallback((option) => {
    if (!option) return;
    if (option.isAdd) {
      handleCreateDna();
      return;
    }
    setSelectedDnaId(option.id);
  }, [handleCreateDna]);

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;

    const confirmed = !isBrowser || window.confirm(t('settings.logout.confirm', { defaultValue: 'Sei sicuro di voler effettuare il logout?' }));
    if (!confirmed) return;

    setOpenPanel(null);
    setIsLoggingOut(true);

    try {
      if (typeof authLogout === 'function') {
        await authLogout();
      }
    } catch (error) {
      console.error('[CardNav] Logout failed', error);
    } finally {
      if (typeof onNavigate === 'function') {
        onNavigate('/desktop/login');
      } else if (isBrowser) {
        window.location.href = '/desktop/login';
      }
      setIsLoggingOut(false);
    }
  }, [authLogout, isLoggingOut, onNavigate]);

  const handleNavigate = useCallback((event, link) => {
    if (link?.action === 'logout') {
      event?.preventDefault();
      handleLogout();
      return;
    }

    if (typeof onNavigate === 'function') {
      event.preventDefault();
      onNavigate(link?.href || '#');
    }
    setOpenPanel(null);
  }, [handleLogout, onNavigate]);

  const handleBatteryClick = useCallback((batteryData) => {
    if (typeof onBatteryClick === 'function') {
      onBatteryClick(batteryData);
    }
    if (typeof onNavigate === 'function') {
      onNavigate('/desktop/battery');
    } else if (isBrowser) {
      window.location.href = '/desktop/battery';
    }
    setOpenPanel(null);
  }, [onBatteryClick, onNavigate]);

  const handleProfileClick = useCallback((companyData) => {
    if (typeof onCtaClick === 'function') {
      onCtaClick(companyData);
    } else if (typeof onNavigate === 'function') {
      onNavigate(PROFILE_PATH);
    } else if (isBrowser) {
      window.location.href = PROFILE_PATH;
    }
    setOpenPanel(null);
  }, [onCtaClick, onNavigate]);

  useEffect(() => {
    if (currentDnaId) {
      setSelectedDnaId(currentDnaId);
    }
  }, [currentDnaId]);

  useEffect(() => {
    if (!isExpanded || !hasDocument) return undefined;

    let blurTimerId = null;

    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenPanel(null);
      }
    };

    const handleWindowBlur = () => {
      if (!isBrowser) return;
      if (blurTimerId) {
        window.clearTimeout(blurTimerId);
      }

      blurTimerId = window.setTimeout(() => {
        const activeElement = document.activeElement;
        if (!activeElement || activeElement.tagName !== 'IFRAME') return;
        if (navRef.current?.contains(activeElement)) return;
        setOpenPanel(null);
      }, 0);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    if (isBrowser) {
      window.addEventListener('blur', handleWindowBlur);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      if (isBrowser) {
        window.removeEventListener('blur', handleWindowBlur);
      }
      if (blurTimerId && isBrowser) {
        window.clearTimeout(blurTimerId);
      }
    };
  }, [isExpanded]);

  return (
    <div className={containerClasses} ref={navRef}>
      <nav className={`card-nav ${isExpanded ? 'open' : ''}`} aria-label={t('desktop.nav.primaryAria', { defaultValue: 'Primary navigation' })}>
        <div className="card-nav-top">
          <button
            type="button"
            className={`hamburger-menu ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-label={
              isMenuOpen
                ? t('desktop.nav.menu.close', { defaultValue: 'Close menu' })
                : t('desktop.nav.menu.open', { defaultValue: 'Open menu' })
            }
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>

          <a
            className="logo-container"
            href={HOME_PATH}
            aria-label={t('desktop.nav.goHome', { defaultValue: 'Torna alla home' })}
            onClick={handleLogoClick}
          >
            <img src={logo} alt={resolvedLogoAlt} className="logo" />
          </a>

          <div className="card-nav-actions">
            <DesktopAIBattery
              onBatteryClick={handleBatteryClick}
              isRegenerating={isAIBatteryRegenerating}
              showStatus={showAIBatteryStatus}
            />
            <button
              type="button"
              className="card-nav-avatar"
              aria-label={resolvedCtaLabel}
              aria-expanded={isProfileOpen}
              onClick={toggleProfilePanel}
            >
              <ProfileAvatar
                showUserPlaceholder={!isAuthenticated}
                isAuthenticated={isAuthenticated}
                userId={user?.id || null}
                onLogoDataChange={setProfileLogoSnapshot}
                onCompanyDataChange={setProfileCompanyData}
              />
            </button>
          </div>
        </div>

        <div className={`card-nav-content ${isProfileOpen ? 'card-nav-content--profile' : ''}`} aria-hidden={!isExpanded}>
          {isMenuOpen && visibleItems.map((item, idx) => (
            <div key={`${item.label}-${idx}`} className={`nav-card nav-card-theme-${idx + 1}`}>
              <div className="nav-card-label">
                {item.labelKey ? t(item.labelKey, { defaultValue: item.label }) : item.label}
              </div>
              <div className="nav-card-links">
                {(item.links || []).map((link, linkIdx) => {
                  const resolvedLabel = link.labelKey ? t(link.labelKey, { defaultValue: link.label }) : link.label;
                  const resolvedAriaLabel = link.ariaLabelKey
                    ? t(link.ariaLabelKey, { defaultValue: link.ariaLabel || resolvedLabel })
                    : (link.ariaLabel || resolvedLabel);
                  const resolvedIconAlt = link.icon?.altKey
                    ? t(link.icon.altKey, { defaultValue: link.icon.alt || resolvedLabel })
                    : (link.icon?.alt || resolvedLabel);
                  const isProfileLink = link?.href === PROFILE_PATH || link?.labelKey === 'desktop.nav.links.profile';

                  return (
                    <a
                      key={`${link.label}-${linkIdx}`}
                      className="nav-card-link"
                      href={link.href || '#'}
                      aria-label={resolvedAriaLabel}
                      onClick={(event) => handleNavigate(event, link)}
                    >
                      <span className="nav-card-link-label">{resolvedLabel}</span>
                      {link.icon ? (
                        <img
                          src={link.icon.src}
                          alt={resolvedIconAlt}
                          className="nav-card-link-icon nav-card-link-icon-image"
                          aria-hidden="true"
                        />
                      ) : isProfileLink ? (
                        <UserPlaceholderIcon className="nav-card-link-icon" />
                      ) : (
                        <ArrowUpRightIcon />
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
          {isProfileOpen && (
            <div className="card-nav-profile-panel" aria-label={t('desktop.nav.profileMenuAria', { defaultValue: 'Apri il menu profilo' })}>
              <div className="card-nav-profile-panel-col card-nav-profile-panel-col--wide card-nav-profile-panel-col--dna nav-card nav-card-theme-1">
                <div className="nav-card-label">
                  {t('desktop.nav.profileMenu.dnaTitle', { defaultValue: 'Scelta DNA Aziendale' })}
                </div>
                <div className="card-nav-profile-dna-body">
                  <div className="card-nav-dna-scroll" role="listbox" aria-label={t('desktop.nav.profileMenu.dnaList', { defaultValue: 'Seleziona DNA' })}>
                    {dnaOptions.map((option) => {
                      const isSelected = option.id === selectedDnaId;
                      const isAdd = option.isAdd;
                      const label = option.label || currentDnaName;

                      return (
                        <button
                          key={option.id}
                          type="button"
                          className={`card-nav-dna-item ${isAdd ? 'card-nav-dna-item-add' : ''}`}
                          data-selected={isSelected || undefined}
                          aria-pressed={isSelected}
                          aria-label={label}
                          onClick={() => handleSelectDna(option)}
                        >
                          {isAdd ? (
                            <span className="card-nav-dna-plus" aria-hidden="true">+</span>
                          ) : (
                            <>
                              <span className="card-nav-dna-fallback" aria-hidden="true">
                                {label?.[0]?.toUpperCase() || 'D'}
                              </span>
                              {option.src && !option.isEmpty && (
                                <img
                                  src={option.src}
                                  alt={label}
                                  className="card-nav-dna-image"
                                  style={{ objectFit: profileLogoFit }}
                                />
                              )}
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="card-nav-profile-panel-col card-nav-profile-panel-col--actions nav-card nav-card-theme-3">
                <div className="nav-card-label">
                  {t('desktop.nav.sections.account', { defaultValue: 'Account & Sistema' })}
                </div>
                <div className="nav-card-links">
                  <button
                    type="button"
                    className="nav-card-link card-nav-profile-link"
                    onClick={() => handleProfileClick(profileCompanyData)}
                  >
                    <span className="nav-card-link-label">
                      {t('desktop.nav.profileMenu.profile', { defaultValue: 'Profilo' })}
                    </span>
                    <UserPlaceholderIcon className="nav-card-link-icon" />
                  </button>
                  <button
                    type="button"
                    className="nav-card-link card-nav-profile-link"
                    onClick={handleLogout}
                  >
                    <span className="nav-card-link-label">
                      {t('desktop.nav.profileMenu.logout', { defaultValue: 'Logout' })}
                    </span>
                    <img
                      src={iconLogout}
                      alt={t('desktop.nav.links.logout', { defaultValue: 'Logout' })}
                      className="nav-card-link-icon nav-card-link-icon-image"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

export default CardNav;
